import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_LINKS, PHONE, PHONE_LINK } from "@/data/site";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled || open
          ? "bg-[#0B0F14]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between gap-4">
        <Link to="/" aria-label="Orego Tours home">
          <Logo compact={scrolled} />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors duration-300 hover:text-[#F2C230] ${
                  isActive ? "text-[#F2C230]" : "text-[#F5F5F0]/75"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={PHONE_LINK}
            data-testid="nav-phone"
            className="hidden xl:flex items-center gap-2 text-sm font-semibold text-[#F5F5F0]/80 hover:text-[#3FB6D3] transition-colors duration-300"
          >
            <Phone size={16} className="text-[#3FB6D3]" />
            {PHONE}
          </a>
          <Link to="/contact" data-testid="nav-cta-book-now" className="btn-gold hidden sm:inline-flex !px-6 !py-2.5">
            Book Now
          </Link>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 rounded-full border border-white/15 text-[#F5F5F0]"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="lg:hidden overflow-hidden bg-[#0B0F14]/95 backdrop-blur-xl border-b border-white/10"
            aria-label="Mobile"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-${l.testid}`}
                  className={({ isActive }) =>
                    `text-base font-medium ${isActive ? "text-[#F2C230]" : "text-[#F5F5F0]/80"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link to="/contact" data-testid="mobile-nav-cta-book-now" className="btn-gold w-full mt-2">
                Book Now
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
